<template>
  <div class="content">
    <progress-bar :percent="20" />
    <div class="title">
      Add a Site <BlackISVG @click="note1Visible = true" />
    </div>

    <a-form-model ref="form" :colon="false" :rules="rules" :model="form">
      <a-form-model-item label="Address" prop="address">
        <template v-if="useIdealPostcodes">
          <a-input
            id="address-input"
            v-model.trim="form.address"
            size="large"
            class="input-box"
            data-tn="system-building-address-solshare-address"
            @input="onIdealInput"
            @blur="placeInputBlurred"
          />
          <IdealPostcodesAutoComplete
            :value="form.address"
            :checked="true"
            input-id="address-input"
            address-mode="full_address"
            default-country-iso3="GBR"
            @input="onIdealInput"
            @address-selected="idealAddressSelected"
          />
        </template>
        <google-autocomplete
          v-else
          id="address-input"
          v-model.trim="form.address"
          classname="input-box"
          :country="Object.values(COUNTRY_CODE)"
          :enable-geolocation="true"
          data-tn="system-building-address-solshare-address"
          @placechanged="placeChanged"
          @blur="placeInputBlurred"
          @change="placeInputChanged"
        >
        </google-autocomplete>
      </a-form-model-item>

      <a-form-model-item
        v-if="showCountryInput"
        prop="postcode"
        label="Postcode"
      >
        <a-input
          v-model.trim="form.postcode"
          size="large"
          class="input-box"
          placeholder="Please input postcode"
          data-tn="postcode-input"
        />
      </a-form-model-item>

      <a-form-model-item v-if="showCountryInput" prop="country" label="Country">
        <a-select
          v-model.trim="form.country"
          size="large"
          class="input-box"
          placeholder="Please input country"
          data-tn="system-building-address-country"
        >
          <a-select-option
            v-for="(code, country) in COUNTRY_CODE"
            :key="code"
            :value="code"
            :data-tn="`system-building-address-country-${code}`"
          >
            {{ country }}
          </a-select-option>
        </a-select>
      </a-form-model-item>
    </a-form-model>

    <a-row type="flex" align="middle">
      <a-col :span="12">
        <back-button :style="{ marginTop: 0 }" />
      </a-col>
      <a-col :span="12">
        <yellow-button
          data-tn="building-address-next"
          :disabled="isSaveDisabled"
          @click="nextClicked"
        >
          NEXT
        </yellow-button>
      </a-col>
    </a-row>

    <yellow-modal v-model="note1Visible" icon padding="62px 26px 21px 26px">
      <div :style="{ fontSize: '21px', marginBottom: '1em' }">
        Only enter the main building address details. Do not include any
        individual unit details here.
      </div>
    </yellow-modal>
  </div>
</template>

<script>
import BlackISVG from "../assets/svg/black-i-filled.svg";
import IdealPostcodesAutoComplete from "../components/IdealPostcodesAutoComplete";
import { COUNTRY_CODE } from "../util/constants";
import {
  isIdealPostcodesEnabled,
  isUnitedKingdomCountryCode,
} from "../util/ideal-postcodes";

export default {
  components: {
    BlackISVG,
    IdealPostcodesAutoComplete,
  },
  data() {
    return {
      COUNTRY_CODE,
      note1Visible: false,
      rules: {
        address: [
          {
            required: true,
            message: "Please input address",
            trigger: "blur",
          },
        ],
        postcode: [
          {
            required: true,
            message: "Please input postcode",
            trigger: "blur",
          },
        ],
        country: [
          {
            required: true,
            message: "Please select country",
            trigger: "blur",
          },
        ],
      },
      showCountryInput: false,
      isSaveDisabled: false,

      // The last address text that came from a suggestion (place_changed)
      lastSelectedAddress: this.$store.state.project.address.address || "",
      // Postcode last returned by Ideal Postcodes, used when manual edits clear coords
      lastIdpcPostcode: this.$store.state.project.address.postcode || null,
      // When true, the next change event was caused by place_changed, not manual typing
      selectedFromSuggestion: false,
    };
  },
  computed: {
    form: {
      get() {
        return this.$store.state.project.address;
      },
      // Intentionally no-op: prevent replacing the whole form object.
      // Consumers should mutate fields on the referenced store object instead.
      set() {},
    },
    useIdealPostcodes() {
      return (
        isIdealPostcodesEnabled() &&
        isUnitedKingdomCountryCode(this.$store.state.userCountryCodeDetected)
      );
    },
  },
  async mounted() {
    if (this.$store.state.userCountryCodeDetected == null) {
      await this.$store.dispatch("getUserLocation");
    }

    // when user hits back from next page, if there's address but no latitude,
    // display country input
    this.showCountryInput =
      this.$store.state.project.address.address &&
      !this.$store.state.project.address.latitude;
  },
  methods: {
    nextClicked() {
      this.$refs.form.validate(async (valid) => {
        if (valid) {
          // Commit current address. Do not clear coords here; they are managed by input handlers.
          this.$store.commit("setAddress", this.form);
          await this.$router.push("/add-warranty-info");
        }
      });
    },
    onIdealInput(value) {
      if (this.selectedFromSuggestion) {
        this.selectedFromSuggestion = false;
        return;
      }
      this.placeInputChanged(value);
    },
    placeInputChanged(newText) {
      // Google flow: selecting a suggestion triggers place_changed first,
      // then the component triggers a change with the final text.
      // We short-circuit that change so we do not clear coordinates just written by place_changed.
      if (this.selectedFromSuggestion) {
        this.selectedFromSuggestion = false;
        return;
      }
      // Manual typing diverges from lastSelectedAddress: clear stale coords and show country select.
      if (newText !== this.lastSelectedAddress) {
        this.showCountryInput = true;

        this.form.latitude = null;
        this.form.longitude = null;

        const idpcPostcode = this.useIdealPostcodes
          ? this.lastIdpcPostcode?.trim()
          : null;
        if (idpcPostcode) {
          this.form.postcode = idpcPostcode;
          this.form.country = COUNTRY_CODE["United Kingdom"];
        } else {
          this.form.country = null;
          this.form.postcode = null;
        }
      }
    },
    placeInputBlurred() {
      this.isSaveDisabled = true;

      // Do not clear coordinates on blur. Timing differs across browsers;
      // instead, delay UI updates to allow a pending place_changed to land.
      // After the delay, if there is still no country, we ask user to select it.
      setTimeout(() => {
        if (!this.form.country || !this.form.postcode) {
          this.showCountryInput = true;
        }
        this.isSaveDisabled = false;
      }, 500);
    },
    idealAddressSelected(address) {
      const addressLine = [
        address?.line_1,
        address?.line_2,
        address?.line_3,
        address?.post_town,
        address?.postcode,
      ]
        .filter(Boolean)
        .map((s) => s.trim())
        .join(", ");
      this.$store.commit("setAddress", {
        address: addressLine,
        latitude: address.latitude ?? null,
        longitude: address.longitude ?? null,
        country: COUNTRY_CODE["United Kingdom"],
        postcode: address.postcode ?? null,
      });
      this.selectedFromSuggestion = true;
      this.lastSelectedAddress = addressLine;
      this.lastIdpcPostcode = address.postcode?.trim() || null;
      this.showCountryInput = false;
    },
    placeChanged(payload, place) {
      const { latitude, longitude, country, postal_code } = payload;
      const addressLine =
        place && place.formatted_address
          ? place.formatted_address.trim()
          : this.form.address;
      this.$store.commit("setAddress", {
        address: addressLine,
        latitude,
        longitude,
        country: COUNTRY_CODE[country],
        postcode: postal_code,
      });
      // Mark that the next change event originates from suggestion selection
      this.selectedFromSuggestion = true;
      // Keep the selected address text as the new baseline for manual-change detection
      this.lastSelectedAddress = addressLine;
      // Show country input only if we could not map a country code or postcode from Google data
      this.showCountryInput = !this.form.country || !this.form.postcode;
    },
  },
};
</script>

<style lang="less" scoped>
.content {
  .title {
    line-height: 38px;
    margin-top: 20px;
    margin-bottom: 27px;
  }
}
</style>
